import React, { useState, useCallback } from 'react';
import LeftSidebar from '../components/LeftSidebar';
import MainCanvas from '../components/MainCanvas';
import RightSidebar from '../components/RightSidebar';
import Toolbar from '../components/Toolbar';
import LockOverlay from '../components/LockOverlay';
import { usePDF } from '../context/PDFContext';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { PanelLeft, PanelRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const EditorPage = () => {
  const { pdfInfo, currentTool, pendingText, pendingImage } = usePDF();
  const navigate = useNavigate();
  const [showLeftPanel, setShowLeftPanel] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(false);

  useEffect(() => {
    if (!pdfInfo) navigate('/');
  }, [pdfInfo, navigate]);

  // Auto-close right panel when user has pending placement (so they can tap the canvas)
  useEffect(() => {
    if (pendingText || pendingImage) {
      setShowRightPanel(false);
    }
  }, [pendingText, pendingImage]);

  // Auto-open right panel when a tool is selected (mobile)
  useEffect(() => {
    if (currentTool) {
      setShowRightPanel(true);
      setShowLeftPanel(false);
    }
  }, [currentTool]);

  if (!pdfInfo) return null;

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <Toolbar />

      {/* Mobile toggle buttons — hidden on lg+ */}
      <div className="lg:hidden flex items-center gap-2 px-3 py-2 border-b border-border bg-surface shrink-0">
        <Button
          variant={showLeftPanel ? 'default' : 'outline'}
          size="sm"
          className="h-10 gap-2 text-xs"
          onClick={() => { setShowLeftPanel(p => !p); setShowRightPanel(false); }}
          data-testid="toggle-left-panel"
        >
          <PanelLeft className="w-4 h-4" />
          Pages
        </Button>
        <Button
          variant={showRightPanel ? 'default' : 'outline'}
          size="sm"
          className="h-10 gap-2 text-xs"
          onClick={() => { setShowRightPanel(p => !p); setShowLeftPanel(false); }}
          data-testid="toggle-right-panel"
        >
          <PanelRight className="w-4 h-4" />
          Tools
        </Button>
        {currentTool && (
          <span className="ml-auto text-xs font-medium text-primary capitalize">{currentTool}</span>
        )}
      </div>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Pay-before-edit lock: blurs sidebars + canvas until signed in / unlocked */}
        <LockOverlay />

        {/* ========================================
            LEFT SIDEBAR — single instance, CSS-responsive
            Desktop: static in flex flow (lg:relative lg:block)
            Mobile: fixed overlay when open, hidden when closed
           ======================================== */}
        <div
          className={`
            shrink-0
            ${showLeftPanel ? 'fixed inset-0 z-50 flex' : 'hidden'}
            lg:relative lg:inset-auto lg:z-auto lg:block lg:flex-none
          `}
        >
          {/* Sidebar content */}
          <div className={`
            bg-background h-full flex flex-col
            ${showLeftPanel ? 'w-64 max-w-[75vw] border-r border-border shadow-2xl' : ''}
            lg:shadow-none lg:w-auto lg:max-w-none
          `}>
            {/* Mobile close button */}
            {showLeftPanel && (
              <div className="lg:hidden flex items-center justify-end px-3 py-2 border-b border-border shrink-0">
                <Button
                  variant="ghost" size="sm" className="h-7 w-7 p-0"
                  onClick={() => setShowLeftPanel(false)}
                  data-testid="close-left-panel"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              <LeftSidebar />
            </div>
          </div>
          {/* Backdrop — mobile only */}
          {showLeftPanel && (
            <div
              className="flex-1 bg-black/40 lg:hidden"
              onClick={() => setShowLeftPanel(false)}
            />
          )}
        </div>

        {/* MAIN CANVAS — always visible */}
        <MainCanvas />

        {/* ========================================
            RIGHT SIDEBAR — single instance, CSS-responsive
            Desktop: static in flex flow (lg:relative lg:block)
            Mobile: fixed overlay when open, hidden when closed
           ======================================== */}
        <div
          className={`
            shrink-0
            ${showRightPanel ? 'fixed inset-0 z-50 flex justify-end' : 'hidden'}
            lg:relative lg:inset-auto lg:z-auto lg:block lg:flex-none
          `}
        >
          {/* Backdrop — mobile only */}
          {showRightPanel && (
            <div
              className="flex-1 bg-black/40 lg:hidden"
              onClick={() => setShowRightPanel(false)}
            />
          )}
          {/* Sidebar content */}
          <div className={`
            bg-background h-full flex flex-col
            ${showRightPanel ? 'w-72 max-w-[80vw] border-l border-border shadow-2xl' : ''}
            lg:shadow-none lg:w-auto lg:max-w-none
          `}>
            {/* Mobile close button */}
            {showRightPanel && (
              <div className="lg:hidden flex items-center justify-end px-3 py-2 border-b border-border shrink-0">
                <Button
                  variant="ghost" size="sm" className="h-7 w-7 p-0"
                  onClick={() => setShowRightPanel(false)}
                  data-testid="close-right-panel"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              <RightSidebar />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorPage;
